<script setup lang="ts">
defineOptions({ name: 'Dashboard' })

import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { Search } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getProjects, createProject, createProjectRequestId, listMemberPickerDefaultScope, listProjectMemberCandidates, suspendProjects, resumeProjects, voidProjects } from '@/api/projectApi'
import type { Project, CreateProjectPayload, ProjectMemberOption } from '@/api/projectApi'
import MemberOrgPickerDialog from '@/components/MemberOrgPickerDialog.vue'
import ProjectOverviewDialog from '@/components/ProjectOverviewDialog.vue'
import { useAppShellStore } from '@/stores/useAppShellStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { usePermissionStore } from '@/stores/usePermissionStore'
import { isChunkLoadError, recoverFromChunkLoadError } from '@/utils/chunkLoadRecovery'
import { PROJECT_CREATE_NO_ACCESS_MESSAGE } from '@/utils/projectCreateReadiness'
import { canAccessProjectCreateRoute } from '@/utils/projectCreateRouteAccess'
import { navigateToProjectCreate } from '@/utils/navigateProjectCreate'
import { PROJECT_VOID_CONFIRM_TOKEN } from '@/utils/projectLifecycle'

const router = useRouter()
const shellStore = useAppShellStore()

const authStore = useAuthStore()
const permissionStore = usePermissionStore()
const currentUser = computed(() => authStore.currentUser)
const canCreateProject = computed(() => canAccessProjectCreateRoute(permissionStore))

// 视图模式：list-列表视图 stage-阶段视图
const viewMode = ref('list')

// 项目列表数据（从 API 加载）
const projects = ref<Project[]>([])
const loading = ref(false)
const loadError = ref<string | null>(null)

// 项目概览数据（基于列表计算）
const projectOverview = computed(() => {
  const list = projects.value
  const inProgress = list.filter(p => {
    const s = String(p.status || '').toUpperCase()
    return s === 'IN_PROGRESS' || s === 'NORMAL' || s === 'PLANNED' || s === 'SUSPENDED' || s === 'DELAYED'
  }).length
  const completed = list.filter(p => {
    const s = String(p.status || '').toUpperCase()
    return s === 'COMPLETED' || s === 'CLOSED'
  }).length
  return {
    totalProjects: list.length,
    inProgress,
    completed,
    totalBudget: 0,
    totalCost: 0,
    utilizationRate: 0
  }
})

// 列表范围：所有项目 / 我的项目
const projectScope = ref<'all' | 'mine'>('mine')
const projectSearchKeyword = ref('')
const memberNameByUserId = ref<Map<number, string>>(new Map())
const overviewDialogOpen = ref(false)

// 新建项目弹框相关状态
const showCreateModal = ref(false)
const creatingProject = ref(false)
const createRequestId = ref('')
const lastSubmittedPayloadFingerprint = ref('')
const newProjectName = ref('')
const newProjectCode = ref('')
const newProjectOwner = ref('')
const newProjectDeliveryDate = ref('')
const newProjectLeadUserId = ref<number | null>(null)
const createLeadPickerVisible = ref(false)
const memberFlatForCreate = ref<ProjectMemberOption[]>([])

const STORAGE_SCOPE_KEY = 'dashboard_project_scope'
const STORAGE_VIEW_KEY = 'dashboard_view_mode'

// 五阶段：概念、计划、开发、验证、发布（与后端 ipd_phase 一致）
const PHASE_CODE_TO_KEY = { CONCEPT: 'concept', PLAN: 'plan', DEVELOP: 'develop', VERIFY: 'verify', RELEASE: 'release' } as Record<string, string>
const phaseGroups = ref([
  { name: '立项', key: 'concept', color: '#1890ff' },
  { name: '产品设计和开发', key: 'plan', color: '#52c41a' },
  { name: '过程设计与研发', key: 'develop', color: '#fa8c16' },
  { name: '产品过程与确认', key: 'verify', color: '#722ed1' },
  { name: '量产与持续改进', key: 'release', color: '#13c2c2' }
])

// 按阶段分组的项目（按当前阶段 currentPhaseCode 分组）
const projectsByPhase = computed(() => {
  const groups: Record<string, Project[]> = {}
  phaseGroups.value.forEach(phase => {
    groups[phase.key] = filteredProjects.value.filter(p => {
      const code = p.currentPhaseCode || ''
      const key = PHASE_CODE_TO_KEY[code] || code.toLowerCase()
      return key === phase.key
    })
  })
  return groups
})

/** 当前用户是否与项目相关：创建人、负责人或成员列表（与后端 ProjectDTO 字段一致） */
function isMyProjectByFields(project: Project): boolean {
  const uid = currentUser.value?.userId
  if (uid == null || Number.isNaN(Number(uid))) return false
  const id = Number(uid)
  if (project.creatorUserId != null && Number(project.creatorUserId) === id) return true
  if (project.projectLeadUserId != null && Number(project.projectLeadUserId) === id) return true
  const members = project.projectMemberUserIds
  if (members?.length && members.some((m) => Number(m) === id)) return true
  return false
}

function projectLeadDisplayName(project: Project): string {
  const leadId = project.projectLeadUserId
  if (leadId == null) return ''
  return memberNameByUserId.value.get(Number(leadId)) || ''
}

function projectMatchesSearch(project: Project, keyword: string): boolean {
  const kw = keyword.trim().toLowerCase()
  if (!kw) return true
  if ((project.name || '').toLowerCase().includes(kw)) return true
  if ((project.projectCode || '').toLowerCase().includes(kw)) return true
  if (project.id != null && String(project.id).includes(kw)) return true
  if ((project.owner || '').toLowerCase().includes(kw)) return true
  const leadName = projectLeadDisplayName(project)
  if (leadName && leadName.toLowerCase().includes(kw)) return true
  if (project.projectLeadUserId != null && String(project.projectLeadUserId).includes(kw)) return true
  return false
}

const scopedProjects = computed(() => {
  return projects.value.filter((project) => {
    return (
      projectScope.value === 'all' ||
      (project as { isMyProject?: boolean }).isMyProject === true ||
      isMyProjectByFields(project)
    )
  })
})

const filteredProjects = computed(() => {
  const kw = projectSearchKeyword.value
  return scopedProjects.value.filter((project) => {
    if (!projectMatchesSearch(project, kw)) return false
    if (statusTab.value === 'ALL') return true
    return normalizeLifecycleStatus(project.status) === statusTab.value
  })
})

/** 项目统计：默认统计个人参与的全部项目 */
const myProjectsForOverview = computed(() =>
  projects.value.filter(
    (project) =>
      (project as { isMyProject?: boolean }).isMyProject === true || isMyProjectByFields(project),
  ),
)

/** 列表视图：按可视区域宽高动态算 cols×rows，铺满后多余分页 */
const LIST_CARD_MIN_PX = 260
const LIST_CARD_GAP_PX = 16
const LIST_CARD_FALLBACK_H = 160

const listPage = ref(1)
const listPageSize = ref(20)
const projectGridRef = ref<HTMLElement | null>(null)
const projectListScrollRef = ref<HTMLElement | null>(null)
let projectGridObserver: ResizeObserver | null = null

function onProjectSearchSubmit() {
  listPage.value = 1
}

function measureListPageSize() {
  const grid = projectGridRef.value
  const scroll = projectListScrollRef.value
  if (!grid) return
  const width = grid.clientWidth
  const height = scroll?.clientHeight ?? grid.clientHeight
  if (!(width > 0) || !(height > 0)) return
  const cols = Math.max(1, Math.floor((width + LIST_CARD_GAP_PX) / (LIST_CARD_MIN_PX + LIST_CARD_GAP_PX)))
  const firstCard = grid.querySelector('.project-card') as HTMLElement | null
  const cardH = firstCard?.offsetHeight && firstCard.offsetHeight > 0
    ? firstCard.offsetHeight
    : LIST_CARD_FALLBACK_H
  const rows = Math.max(1, Math.floor((height + LIST_CARD_GAP_PX) / (cardH + LIST_CARD_GAP_PX)))
  const next = cols * rows
  if (listPageSize.value !== next) {
    listPageSize.value = next
  }
}

function bindProjectGridObserver() {
  projectGridObserver?.disconnect()
  projectGridObserver = null
  if (typeof ResizeObserver === 'undefined') return
  const targets = [projectListScrollRef.value, projectGridRef.value].filter(Boolean) as HTMLElement[]
  if (!targets.length) return
  projectGridObserver = new ResizeObserver(() => measureListPageSize())
  for (const el of targets) projectGridObserver.observe(el)
  measureListPageSize()
}

const pagedListProjects = computed(() => {
  const all = filteredProjects.value
  const start = (listPage.value - 1) * listPageSize.value
  return all.slice(start, start + listPageSize.value)
})

const showListPagination = computed(() => filteredProjects.value.length > listPageSize.value)

/** 阶段视图：各阶段独立分页 */
const STAGE_PAGE_SIZE = 10
const stagePageByKey = ref<Record<string, number>>({})

function stageProjects(phaseKey: string): Project[] {
  return projectsByPhase.value[phaseKey] ?? []
}

function pagedStageProjects(phaseKey: string): Project[] {
  const all = stageProjects(phaseKey)
  const page = stagePageByKey.value[phaseKey] ?? 1
  const start = (page - 1) * STAGE_PAGE_SIZE
  return all.slice(start, start + STAGE_PAGE_SIZE)
}

function showStagePagination(phaseKey: string): boolean {
  return stageProjects(phaseKey).length > STAGE_PAGE_SIZE
}

function stagePage(phaseKey: string): number {
  return stagePageByKey.value[phaseKey] ?? 1
}

function setStagePage(phaseKey: string, page: number) {
  stagePageByKey.value = { ...stagePageByKey.value, [phaseKey]: page }
}

function resetStagePages() {
  stagePageByKey.value = {}
}

watch([listPageSize, () => filteredProjects.value.length], () => {
  const total = filteredProjects.value.length
  const maxPage = Math.max(1, Math.ceil(total / listPageSize.value) || 1)
  if (listPage.value > maxPage) listPage.value = maxPage
})

watch(
  () => filteredProjects.value.map((p) => `${p.id}:${p.currentPhaseCode ?? ''}`).join('|'),
  () => {
    const next: Record<string, number> = {}
    for (const phase of phaseGroups.value) {
      const total = stageProjects(phase.key).length
      const maxPage = Math.max(1, Math.ceil(total / STAGE_PAGE_SIZE) || 1)
      const cur = stagePageByKey.value[phase.key] ?? 1
      next[phase.key] = Math.min(cur, maxPage)
    }
    stagePageByKey.value = next
  },
)

function memberLabelForCreate(userId: number | null) {
  if (userId == null) return ''
  const m = memberFlatForCreate.value.find((x) => Number(x.userId) === userId)
  return m?.realName || ''
}

const createLeadDisplay = computed(() => {
  const id = newProjectLeadUserId.value
  if (id == null) return '点击选择项目负责人'
  return memberLabelForCreate(id) || `用户 #${id}`
})

async function loadMemberCandidatesForCreate() {
  try {
    const scope = await listMemberPickerDefaultScope()
    memberFlatForCreate.value = scope.members || []
    const lead = newProjectLeadUserId.value
    if (lead != null && !memberFlatForCreate.value.some((m) => Number(m.userId) === Number(lead))) {
      const extra = await listProjectMemberCandidates('', [Number(lead)])
      const byId = new Map(memberFlatForCreate.value.map((m) => [m.userId, m]))
      for (const m of extra || []) byId.set(m.userId, m)
      memberFlatForCreate.value = Array.from(byId.values())
    }
  } catch (e) {
    console.error(e)
    memberFlatForCreate.value = []
  }
}

async function searchCreateMemberFlat(keyword: string) {
  try {
    memberFlatForCreate.value = await listProjectMemberCandidates(keyword || '')
  } catch (e) {
    console.error(e)
  }
}

function onCreateLeadPicked(v: number | number[] | null) {
  if (v == null || v === '') {
    newProjectLeadUserId.value = null
    return
  }
  const n = typeof v === 'number' ? v : Number(v)
  newProjectLeadUserId.value = Number.isNaN(n) ? null : n
}

const openCreateModal = () => {
  if (!canCreateProject.value) {
    ElMessage.warning(PROJECT_CREATE_NO_ACCESS_MESSAGE)
    return
  }
  void navigateToProjectCreate(router)
}

type ProjectDraft = {
  name?: string
  code?: string
  owner?: string
  deliveryDate?: string
  leadUserId?: string
}

function applyExpandedProjectDraft(draft: ProjectDraft) {
  if (draft.name) newProjectName.value = draft.name
  if (draft.code) newProjectCode.value = draft.code
  if (draft.owner) newProjectOwner.value = draft.owner
  if (draft.deliveryDate) newProjectDeliveryDate.value = draft.deliveryDate
  if (draft.leadUserId) {
    const leadId = Number(draft.leadUserId)
    if (!Number.isNaN(leadId)) newProjectLeadUserId.value = leadId
  }
}

watch(
  () => shellStore.pendingExpandedProjectDraft,
  (draft) => {
    if (!draft) return
    applyExpandedProjectDraft(draft)
    shellStore.setPendingExpandedProjectDraft(null)
    openCreateModal()
  },
)

watch(
  () => shellStore.openDashboardProjectFormTick,
  (tick, prev) => {
    if (tick <= 0 || tick === prev) return
    openCreateModal()
  },
)

const resetCreateForm = () => {
  newProjectName.value = ''
  newProjectCode.value = ''
  newProjectOwner.value = ''
  newProjectDeliveryDate.value = ''
  newProjectLeadUserId.value = null
  createRequestId.value = ''
  lastSubmittedPayloadFingerprint.value = ''
}

const closeCreateModal = () => {
  if (creatingProject.value) return
  showCreateModal.value = false
  resetCreateForm()
}

const submitNewProject = async () => {
  if (creatingProject.value) return
  if (!canCreateProject.value) {
    ElMessage.warning(PROJECT_CREATE_NO_ACCESS_MESSAGE)
    return
  }
  if (!newProjectName.value.trim()) {
    ElMessage.error('请输入项目名称')
    return
  }
  if (!newProjectOwner.value.trim()) {
    ElMessage.error('请输入客户名称')
    return
  }
  if (!newProjectDeliveryDate.value) {
    ElMessage.error('请选择项目交付日期')
    return
  }

  const leadId = newProjectLeadUserId.value
  const payload: CreateProjectPayload = {
    name: newProjectName.value.trim(),
    owner: newProjectOwner.value.trim(),
    projectDeliveryDate: newProjectDeliveryDate.value,
    createSource: 'HOME',
    ...(leadId != null && !Number.isNaN(Number(leadId)) ? { projectLeadUserId: Number(leadId) } : {}),
  }

  const payloadFingerprint = JSON.stringify(payload)
  if (
    lastSubmittedPayloadFingerprint.value
    && lastSubmittedPayloadFingerprint.value !== payloadFingerprint
  ) {
    createRequestId.value = createProjectRequestId()
  }
  if (!createRequestId.value) createRequestId.value = createProjectRequestId()
  const requestId = createRequestId.value
  lastSubmittedPayloadFingerprint.value = payloadFingerprint
  creatingProject.value = true
  try {
    const project = await createProject(payload, requestId)
    ElMessage.success('项目创建成功')
    showCreateModal.value = false
    resetCreateForm()
    await loadProjects()
    if (project?.id != null) {
      goToProjectDashboard(project.id, { tab: 'info', autoWbs: true })
    }
  } catch (e: any) {
    console.error(e)
    const status = e?.status ?? e?.response?.status
    if (status === 403) {
      ElMessage.error(PROJECT_CREATE_NO_ACCESS_MESSAGE)
    } else {
      ElMessage.error(e?.message || '创建项目失败')
    }
  } finally {
    creatingProject.value = false
  }
}

watch(projectScope, (val) => {
  listPage.value = 1
  resetStagePages()
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_SCOPE_KEY, val)
  }
})

watch(projectSearchKeyword, () => {
  listPage.value = 1
  resetStagePages()
})

watch(viewMode, (val) => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_VIEW_KEY, val)
  }
  if (val === 'list') {
    listPage.value = 1
    void nextTick(() => bindProjectGridObserver())
  } else {
    resetStagePages()
    projectGridObserver?.disconnect()
    projectGridObserver = null
  }
})

const statusDisplayMap: Record<string, string> = {
  NORMAL: '正常',
  DELAYED: '延期',
  SUSPENDED: '挂起',
  VOID: '作废',
  TERMINATED: '作废',
  CLOSED: '已完成',
  ACTIVE: '正常',
  PLANNED: '正常',
  IN_PROGRESS: '正常',
  COMPLETED: '已完成'
}
const displayStatus = (status: string) => statusDisplayMap[status] || statusDisplayMap[normalizeLifecycleStatus(status)] || '正常'

const STATUS_TABS = [
  { key: 'ALL', label: '全部' },
  { key: 'NORMAL', label: '正常' },
  { key: 'DELAYED', label: '延期' },
  { key: 'SUSPENDED', label: '挂起' },
  { key: 'CLOSED', label: '已完成' },
  { key: 'VOID', label: '作废' },
] as const
const statusTab = ref<(typeof STATUS_TABS)[number]['key']>('ALL')

function normalizeLifecycleStatus(status: string | null | undefined): string {
  const s = String(status || '').toUpperCase()
  if (s === 'ACTIVE' || s === 'PLANNED' || s === 'IN_PROGRESS') return 'NORMAL'
  if (s === 'COMPLETED') return 'CLOSED'
  if (s === 'TERMINATED') return 'VOID'
  if (s === 'NORMAL' || s === 'DELAYED' || s === 'SUSPENDED' || s === 'CLOSED' || s === 'VOID') return s
  return 'NORMAL'
}

function statusTabCount(key: string): number {
  if (key === 'ALL') return scopedProjects.value.length
  return scopedProjects.value.filter((p) => normalizeLifecycleStatus(p.status) === key).length
}

function canLifecycleProject(project: Project): boolean {
  if (permissionStore.hasRoles('SUPER_ADMIN')) return true
  const uid = currentUser.value?.userId
  if (uid == null || project.projectLeadUserId == null) return false
  return Number(project.projectLeadUserId) === Number(uid)
}

function cardLifecycleActions(project: Project): Array<'suspend' | 'resume' | 'void'> {
  if (!canLifecycleProject(project)) return []
  const st = normalizeLifecycleStatus(project.status)
  if (st === 'NORMAL' || st === 'DELAYED') return ['suspend', 'void']
  if (st === 'SUSPENDED') return ['resume', 'void']
  return []
}

async function reloadProjectsQuiet() {
  try {
    const res = await getProjects({ page: 1, size: 200 })
    projects.value = res.records || []
  } catch (e) {
    console.error(e)
  }
}

async function onCardLifecycleCommand(cmd: string, project: Project) {
  if (cmd === 'suspend') await onSuspendProject(project)
  else if (cmd === 'resume') await onResumeProject(project)
  else if (cmd === 'void') await onVoidProject(project)
}

async function onSuspendProject(project: Project) {
  try {
    await ElMessageBox.confirm(
      `确认将项目「${project.name}」挂起？挂起后项目将移至「挂起」标签下，项目相关自动通知将失效。`,
      '挂起确认',
      { type: 'warning' },
    )
    await suspendProjects([project.id])
    ElMessage.success('已挂起')
    await reloadProjectsQuiet()
  } catch (e: any) {
    if (e === 'cancel' || e === 'close') return
    ElMessage.error(e?.message || '挂起失败')
  }
}

const resumeDialogVisible = ref(false)
const resumeDialogSubmitting = ref(false)
const resumeTargetProject = ref<Project | null>(null)
const resumeDeliveryDate = ref('')

function todayDateStr() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function disabledResumeDeliveryDate(date: Date) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return date.getTime() < today.getTime()
}

function onResumeProject(project: Project) {
  resumeTargetProject.value = project
  const existing = String(project.projectDeliveryDate || '').slice(0, 10)
  const today = todayDateStr()
  resumeDeliveryDate.value = existing && existing >= today ? existing : today
  resumeDialogVisible.value = true
}

function closeResumeDialog() {
  if (resumeDialogSubmitting.value) return
  resumeDialogVisible.value = false
  resumeTargetProject.value = null
  resumeDeliveryDate.value = ''
}

async function confirmResumeProject() {
  const project = resumeTargetProject.value
  if (!project) return
  const date = String(resumeDeliveryDate.value || '').trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    ElMessage.warning('请选择项目交付日期')
    return
  }
  if (date < todayDateStr()) {
    ElMessage.warning('交付日期不能早于今天')
    return
  }
  resumeDialogSubmitting.value = true
  try {
    await resumeProjects([project.id], date)
    ElMessage.success('已取消挂起')
    resumeDialogVisible.value = false
    resumeTargetProject.value = null
    resumeDeliveryDate.value = ''
    await reloadProjectsQuiet()
  } catch (e: any) {
    ElMessage.error(e?.message || '取消挂起失败')
  } finally {
    resumeDialogSubmitting.value = false
  }
}

async function onVoidProject(project: Project) {
  try {
    await ElMessageBox.confirm(
      `确认将项目「${project.name}」作废？作废后项目全部信息将锁定为只读，且不允许恢复。`,
      '作废确认',
      { type: 'warning', confirmButtonText: '继续作废', cancelButtonText: '取消' },
    )
    await ElMessageBox.prompt(
      `请输入「${PROJECT_VOID_CONFIRM_TOKEN}」以二次确认。此操作不可恢复。`,
      '二次确认',
      {
        type: 'warning',
        confirmButtonText: '确认作废',
        cancelButtonText: '取消',
        inputPattern: new RegExp(`^${PROJECT_VOID_CONFIRM_TOKEN}$`),
        inputErrorMessage: `请输入「${PROJECT_VOID_CONFIRM_TOKEN}」`,
        inputPlaceholder: PROJECT_VOID_CONFIRM_TOKEN,
      },
    )
    await voidProjects([project.id])
    ElMessage.success('已作废')
    await reloadProjectsQuiet()
  } catch (e: any) {
    if (e === 'cancel' || e === 'close') return
    ElMessage.error(e?.message || '作废失败')
  }
}

// 获取状态类名（支持后端枚举）- 用于标签样式
const getStatusClass = (status: string) => {
  const statusMap: Record<string, string> = {
    NORMAL: 'status-progress',
    DELAYED: 'status-delayed',
    IN_PROGRESS: 'status-progress',
    PLANNED: 'status-progress',
    SUSPENDED: 'status-paused',
    VOID: 'status-void',
    TERMINATED: 'status-void',
    CLOSED: 'status-completed',
    COMPLETED: 'status-completed',
    进行中: 'status-progress',
    已完成: 'status-completed',
    已暂停: 'status-paused',
    计划中: 'status-paused'
  }
  return statusMap[status] || ''
}

// 风险标签样式（高风险-红，中风险-黄，低风险-灰）
const getRiskTagClass = (riskLevel: string) => {
  const map: Record<string, string> = {
    HIGH: 'risk-high',
    MEDIUM: 'risk-medium',
    LOW: 'risk-low'
  }
  return map[riskLevel] || 'risk-low'
}

const riskLevelLabel = (riskLevel: string) => {
  const map: Record<string, string> = {
    HIGH: '高风险',
    MEDIUM: '中风险',
    LOW: '低风险'
  }
  return map[riskLevel] || riskLevel
}

// 格式化日期为 YYYY-MM-DD（贴近 UI 稿）
const formatDate = (val: string | null | undefined) => {
  if (!val) return '—'
  const raw = String(val).trim()
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})/)
  if (m) return m[1]
  try {
    const d = new Date(raw)
    if (Number.isNaN(d.getTime())) return '—'
    const y = d.getFullYear()
    const mo = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${mo}-${day}`
  } catch {
    return '—'
  }
}

// 格式化金额
const formatMoney = (value: number) => {
  if (value == null || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'CNY' }).format(value)
}

async function loadMemberNameIndex(leadIds: number[] = []) {
  try {
    const ids = [...new Set(leadIds.map((n) => Number(n)).filter((n) => !Number.isNaN(n) && n > 0))]
    const members = await listProjectMemberCandidates('', ids.length ? ids : undefined)
    const map = new Map<number, string>()
    for (const m of members) {
      if (m.userId != null) map.set(Number(m.userId), (m.realName || '').trim())
    }
    memberNameByUserId.value = map
  } catch (e) {
    console.error(e)
    memberNameByUserId.value = new Map()
  }
}

// 加载项目列表
const loadProjects = async () => {
  loading.value = true
  loadError.value = null
  try {
    const res = await getProjects({ page: 1, size: 500 })
    projects.value = res.records || []
    const leadIds = projects.value
      .map((p) => p.projectLeadUserId)
      .filter((id): id is number => id != null && !Number.isNaN(Number(id)))
    await loadMemberNameIndex(leadIds.map(Number))
  } catch (e: unknown) {
    loadError.value = e instanceof Error ? e.message : '加载项目列表失败'
    projects.value = []
  } finally {
    loading.value = false
    void nextTick(() => {
      if (viewMode.value === 'list') {
        bindProjectGridObserver()
      }
    })
  }
}

const goToProjectDashboard = async (id: number, opts?: { autoWbs?: boolean; tab?: string }) => {
  const path = `/project/${id}/dashboard`
  const query: Record<string, string> = {}
  if (opts?.autoWbs) query.autoWbs = '1'
  if (opts?.tab) query.tab = opts.tab
  try {
    await router.push({
      name: 'ProjectDashboard',
      params: { id: String(id) },
      ...(Object.keys(query).length ? { query } : {}),
    })
  } catch (e) {
    // 部署换 hash 后旧入口动态加载 ProjectDashboard chunk 会 404
    if (isChunkLoadError(e)) {
      const qs = new URLSearchParams(query).toString()
      recoverFromChunkLoadError(qs ? `${path}?${qs}` : path)
      return
    }
    throw e
  }
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    const savedScope = window.localStorage.getItem(STORAGE_SCOPE_KEY)
    if (savedScope === 'all' || savedScope === 'mine') {
      projectScope.value = savedScope
    }
    const savedView = window.localStorage.getItem(STORAGE_VIEW_KEY)
    if (savedView === 'list' || savedView === 'stage') {
      viewMode.value = savedView
    }
  }
  loadProjects()
  void nextTick(() => {
    if (viewMode.value === 'list') bindProjectGridObserver()
  })
})

onBeforeUnmount(() => {
  projectGridObserver?.disconnect()
  projectGridObserver = null
})
</script>

<template>
  <div class="dashboard-page">
        <!-- 主要内容区：项目列表或阶段视图 -->
        <div class="project-list-panel" v-loading="loading">
        <div v-if="loadError" class="load-error">{{ loadError }}</div>
        <template v-else>
          <!-- 第一行：标题 + 范围 + 视图切换 -->
          <div class="list-header list-header--row1">
            <div class="list-header-left">
              <h3 class="list-title">{{ viewMode === 'list' ? '项目列表' : '项目阶段视图' }}</h3>
              <span class="list-count">共 {{ filteredProjects.length }} 个项目</span>
              <div class="scope-segment">
                <button
                  class="scope-btn"
                  :class="{ active: projectScope === 'all' }"
                  @click="projectScope = 'all'"
                >
                  所有项目
                </button>
                <button
                  class="scope-btn"
                  :class="{ active: projectScope === 'mine' }"
                  @click="projectScope = 'mine'"
                >
                  我的项目
                </button>
              </div>
              <div class="view-toggle">
                <button
                  :class="['toggle-btn', { active: viewMode === 'list' }]"
                  @click="viewMode = 'list'"
                  title="列表视图"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="8" y1="6" x2="21" y2="6"/>
                    <line x1="8" y1="12" x2="21" y2="12"/>
                    <line x1="8" y1="18" x2="21" y2="18"/>
                    <line x1="3" y1="6" x2="3.01" y2="6"/>
                    <line x1="3" y1="12" x2="3.01" y2="12"/>
                    <line x1="3" y1="18" x2="3.01" y2="18"/>
                  </svg>
                </button>
                <button
                  :class="['toggle-btn', { active: viewMode === 'stage' }]"
                  @click="viewMode = 'stage'"
                  title="阶段视图"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="3" width="7" height="7"/>
                    <rect x="14" y="3" width="7" height="7"/>
                    <rect x="14" y="14" width="7" height="7"/>
                    <rect x="3" y="14" width="7" height="7"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <!-- 第二行：状态筛选 + 搜索/操作 -->
          <div class="lifecycle-toolbar">
            <div v-if="viewMode === 'list'" class="status-tabs">
              <button
                v-for="tab in STATUS_TABS"
                :key="tab.key"
                type="button"
                class="status-tab"
                :class="{ active: statusTab === tab.key }"
                @click="statusTab = tab.key; listPage = 1"
              >
                {{ tab.label }}
                <span class="status-tab-count">{{ statusTabCount(tab.key) }}</span>
              </button>
            </div>
            <div v-else class="status-tabs-spacer" />
            <div class="list-header-right">
              <el-input
                v-model="projectSearchKeyword"
                class="project-search-input"
                clearable
                placeholder="请输入你需要的搜索内容"
                @keyup.enter="onProjectSearchSubmit"
              >
                <template #prefix>
                  <button
                    type="button"
                    class="project-search-prefix-btn"
                    aria-label="搜索"
                    @click="onProjectSearchSubmit"
                  >
                    <el-icon><Search /></el-icon>
                  </button>
                </template>
              </el-input>
              <button
                v-if="canCreateProject"
                type="button"
                class="overview-entry-btn overview-entry-btn--primary"
                @click="openCreateModal"
              >
                新建项目
              </button>
              <button type="button" class="overview-entry-btn" @click="overviewDialogOpen = true">
                项目统计
              </button>
            </div>
          </div>
          <div ref="projectListScrollRef" class="project-list-scroll">
        <!-- 列表视图 -->
        <template v-if="viewMode === 'list'">
          
          <div ref="projectGridRef" class="project-grid">
            <div 
              v-for="project in pagedListProjects" 
              :key="project.id" 
              class="project-card project-card-redesign"
              :style="{ '--card-progress': Math.min(100, Math.max(0, project.progress ?? 0)) + '%' }"
              @click="goToProjectDashboard(project.id)"
            >
              <div class="project-card-top">
                <div class="project-card-tags">
                  <span :class="['project-card-tag', 'tag-status', getStatusClass(project.status)]">
                    {{ displayStatus(project.status) }}
                  </span>
                  <span
                    v-if="project.riskLevel"
                    :class="['project-card-tag', 'tag-risk', getRiskTagClass(project.riskLevel)]"
                  >
                    {{ riskLevelLabel(project.riskLevel) }}
                  </span>
                </div>
                <el-dropdown
                  v-if="cardLifecycleActions(project).length"
                  class="project-card-more"
                  trigger="click"
                  @command="(cmd: string) => onCardLifecycleCommand(cmd, project)"
                  @click.stop
                >
                  <span class="project-card-more-btn" @click.stop>⋯</span>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item v-if="cardLifecycleActions(project).includes('suspend')" command="suspend">挂起</el-dropdown-item>
                      <el-dropdown-item v-if="cardLifecycleActions(project).includes('resume')" command="resume">取消挂起</el-dropdown-item>
                      <el-dropdown-item v-if="cardLifecycleActions(project).includes('void')" command="void">作废</el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
              <h4 class="project-card-name" :title="project.name">{{ project.name }}</h4>
              <div class="project-card-row project-card-row--ellipsis-value">
                <span class="project-card-label">项目编号</span>
                <span class="project-card-value project-card-value--ellipsis" :title="project.projectCode || (project.id != null ? String(project.id) : '—')">{{ project.projectCode || (project.id != null ? String(project.id) : '—') }}</span>
              </div>
              <div class="project-card-row project-card-row--ellipsis-value">
                <span class="project-card-label">负责人</span>
                <span class="project-card-value project-card-value--ellipsis" :title="projectLeadDisplayName(project) || '—'">{{ projectLeadDisplayName(project) || '—' }}</span>
              </div>
              <div class="project-card-row project-card-row--ellipsis-value">
                <span class="project-card-label">交付日期</span>
                <span class="project-card-value project-card-value--ellipsis" :title="formatDate(project.projectDeliveryDate)">{{ formatDate(project.projectDeliveryDate) }}</span>
              </div>
              <div class="project-card-row project-card-row--ellipsis-value">
                <span class="project-card-label">客户名称</span>
                <span class="project-card-value project-card-value--ellipsis" :title="project.owner || '—'">{{ project.owner || '—' }}</span>
              </div>
            </div>
          </div>
          <div v-if="showListPagination" class="project-list-pagination">
            <el-pagination
              v-model:current-page="listPage"
              :page-size="listPageSize"
              :total="filteredProjects.length"
              layout="total, prev, pager, next, jumper"
              background
              hide-on-single-page
            />
          </div>
        </template>
        
        <!-- 阶段视图 -->
        <template v-else>
          
          <div class="stage-view">
            <div 
              v-for="phase in phaseGroups" 
              :key="phase.key" 
              class="stage-column"
            >
              <div class="stage-header">
                <div class="stage-header-main">
                  <div class="stage-title">
                    <span class="stage-dot" :style="{ backgroundColor: phase.color }"></span>
                    <span class="stage-title-text">{{ phase.name }}阶段</span>
                  </div>
                  <span class="stage-count">{{ projectsByPhase[phase.key]?.length || 0 }} 个项目</span>
                </div>
                <div class="stage-header-bar" :style="{ backgroundColor: phase.color }" aria-hidden="true" />
              </div>
              
              <div class="stage-body">
                <div class="stage-cards">
                  <div 
                    v-for="project in pagedStageProjects(phase.key)" 
                    :key="project.id" 
                    class="stage-card stage-card--progress"
                    :style="{ '--card-progress': Math.min(100, Math.max(0, project.progress ?? 0)) + '%' }"
                    @click="goToProjectDashboard(project.id)"
                  >
                    <div class="stage-card-header">
                      <h4 class="stage-card-title" :title="project.name">{{ project.name }}</h4>
                      <span :class="['status-badge', 'stage-card-status', getStatusClass(project.status)]">
                        {{ displayStatus(project.status) }}
                      </span>
                    </div>
                    
                    <div class="stage-card-info">
                      <div class="info-row stage-card-info-row">
                        <span class="info-label">项目编号：</span>
                        <span class="info-value info-value--ellipsis" :title="project.projectCode || '—'">{{ project.projectCode || '—' }}</span>
                      </div>
                      <div class="info-row stage-card-info-row">
                        <span class="info-label">客户名称：</span>
                        <span class="info-value info-value--ellipsis" :title="project.owner || '—'">{{ project.owner || '—' }}</span>
                      </div>
                      <div class="info-row stage-card-info-row">
                        <span class="info-label">项目交付日期：</span>
                        <span class="info-value info-value--fixed">{{ formatDate(project.projectDeliveryDate) }}</span>
                      </div>
                    </div>
                    
                    <div class="stage-card-budget" v-if="project.phaseDeliveryDates?.length">
                      <span
                        v-for="pd in project.phaseDeliveryDates"
                        :key="pd.phaseCode"
                        class="phase-date-item"
                        :title="`${pd.phaseCode === 'PLAN' ? '产品设计和开发' : pd.phaseName}：${formatDate(pd.plannedEndTime)}`"
                      >
                        {{ pd.phaseCode === 'PLAN' ? '产品设计和开发' : pd.phaseName }}：{{ formatDate(pd.plannedEndTime) }}
                      </span>
                    </div>
                  </div>

                  <div v-if="!projectsByPhase[phase.key]?.length" class="stage-empty">
                    暂无项目
                  </div>
                </div>

                <div v-if="showStagePagination(phase.key)" class="stage-column-pagination">
                  <el-pagination
                    :current-page="stagePage(phase.key)"
                    :page-size="STAGE_PAGE_SIZE"
                    :total="stageProjects(phase.key).length"
                    layout="prev, pager, next"
                    size="small"
                    background
                    hide-on-single-page
                    @current-change="(p: number) => setStagePage(phase.key, p)"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>
          </div>
        </template>
      </div>
      <!-- 新建项目弹框 -->
      <div v-if="showCreateModal" class="project-modal-mask">
        <div class="project-modal-wrapper">
          <div class="project-modal-container">
            <div class="project-modal-header">
              <div class="project-modal-title">新建项目</div>
              <button class="project-modal-close" @click="closeCreateModal">×</button>
            </div>
            <div class="project-modal-body">
              <div class="project-modal-form-item">
                <label class="project-modal-label">
                  项目名称<span class="required-star">*</span>
                </label>
                <input
                  v-model="newProjectName"
                  class="project-modal-input"
                  type="text"
                  maxlength="48"
                  placeholder="请输入项目名称"
                />
                <div class="project-modal-word-limit">
                  {{ newProjectName.length }}/48
                </div>
              </div>
              <div class="project-modal-form-item">
                <label class="project-modal-label">
                  客户名称<span class="required-star">*</span>
                </label>
                <input
                  v-model="newProjectOwner"
                  class="project-modal-input"
                  type="text"
                  maxlength="32"
                  placeholder="请输入客户名称"
                />
                <div class="project-modal-word-limit">
                  {{ newProjectOwner.length }}/32
                </div>
              </div>
              <div class="project-modal-form-item">
                <label class="project-modal-label">
                  项目交付日期<span class="required-star">*</span>
                </label>
                <input
                  v-model="newProjectDeliveryDate"
                  class="project-modal-input"
                  type="date"
                />
              </div>
              <div class="project-modal-form-item">
                <label class="project-modal-label">项目负责人</label>
                <div
                  class="project-modal-member-trigger"
                  @click="createLeadPickerVisible = true"
                >
                  {{ createLeadDisplay }}
                </div>
                <div class="project-modal-hint">默认为当前登录用户，可点击修改为其他成员</div>
              </div>
            </div>
            <div class="project-modal-footer">
              <button class="btn" @click="closeCreateModal">取消</button>
              <button
                class="btn btn-primary"
                :disabled="creatingProject"
                @click="submitNewProject"
              >
                {{ creatingProject ? '创建中...' : '新建' }}
              </button>
            </div>
          </div>
        </div>
      </div>

    <ProjectOverviewDialog
      v-model="overviewDialogOpen"
      :projects="myProjectsForOverview"
    />

    <MemberOrgPickerDialog
      v-model:visible="createLeadPickerVisible"
      mode="single"
      :members="memberFlatForCreate"
      title="选择项目负责人"
      :model-value="newProjectLeadUserId"
      :remote-search="true"
      @update:model-value="onCreateLeadPicked"
    />

    <div v-if="resumeDialogVisible" class="project-modal-mask">
      <div class="project-modal-wrapper">
        <div class="project-modal-container">
          <div class="project-modal-header">
            <div class="project-modal-title">取消挂起</div>
            <button class="project-modal-close" :disabled="resumeDialogSubmitting" @click="closeResumeDialog">×</button>
          </div>
          <div class="project-modal-body">
            <p class="resume-dialog-tip">
              是否确认项目「{{ resumeTargetProject?.name }}」取消挂起？请选择恢复后的交付日期，保存后项目状态将变更为「正常」。
            </p>
            <div class="project-modal-form-item">
              <label class="project-modal-label">
                项目交付日期<span class="required-star">*</span>
              </label>
              <el-date-picker
                v-model="resumeDeliveryDate"
                type="date"
                format="YYYY年MM月DD日"
                value-format="YYYY-MM-DD"
                placeholder="选择交付日期"
                style="width: 100%"
                :disabled-date="disabledResumeDeliveryDate"
                :clearable="false"
              />
            </div>
          </div>
          <div class="project-modal-footer">
            <button class="btn" :disabled="resumeDialogSubmitting" @click="closeResumeDialog">取消</button>
            <button
              class="btn btn-primary"
              :disabled="resumeDialogSubmitting || !resumeDeliveryDate"
              @click="confirmResumeProject"
            >
              {{ resumeDialogSubmitting ? '提交中...' : '确认取消挂起' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<style scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.dashboard-page {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* 新建项目弹框样式 */
.project-modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.project-modal-wrapper {
  width: 100%;
  max-width: 640px;
  padding: 0 24px;
}

.project-modal-container {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.16);
  overflow: hidden;
}

.project-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.project-modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2329;
}

.project-modal-close {
  border: none;
  background: transparent;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  color: #999;
}

.project-modal-close:hover {
  color: #555;
}

.project-modal-body {
  padding: 16px 24px 8px;
}

.resume-dialog-tip {
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.6;
  color: #4e5969;
}

.project-modal-form-item {
  margin-bottom: 12px;
}

.project-modal-label {
  display: block;
  font-size: 13px;
  color: #555;
  margin-bottom: 4px;
}

.required-star {
  color: #f5222d;
  margin-left: 2px;
}

.project-modal-input {
  width: 100%;
  padding: 8px 10px;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.project-modal-input:focus {
  border-color: #1890ff;
}

.project-modal-input[disabled] {
  background-color: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

.project-modal-member-trigger {
  width: 100%;
  padding: 8px 10px;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
  font-size: 14px;
  cursor: pointer;
  background: #fff;
  color: #1f2329;
  transition: border-color 0.2s;
}

.project-modal-member-trigger:hover {
  border-color: #1890ff;
}

.project-modal-hint {
  margin-top: 4px;
  font-size: 12px;
  color: #999;
}

.project-modal-word-limit {
  margin-top: 2px;
  font-size: 12px;
  color: #999;
  text-align: right;
}

.project-modal-footer {
  padding: 12px 24px 16px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.project-list-container {
  min-height: 100vh;
  background: #f5f7fa;
  padding: 24px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* 顶部栏（无 breadcrumb） */
.page-header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.project-list-panel {
  width: 100%;
  max-width: 100%;
  position: relative;
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  padding: 16px 20px 20px;
}

.project-list-scroll {
  flex: 1 1 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-color: #b8c3d1 #f3f6fa;
  scrollbar-width: thin;
}

.project-list-scroll::-webkit-scrollbar {
  width: 10px;
}

.project-list-scroll::-webkit-scrollbar-thumb {
  border-radius: 5px;
  background: #b8c3d1;
}

.project-list-scroll::-webkit-scrollbar-track {
  background: #f3f6fa;
}

/* 按钮样式 */
.btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  border: none;
  transition: all 0.3s ease;
}

.btn-primary {
  background: #1890ff;
  color: white;
}

.btn-primary:hover {
  background: #40a9ff;
}

.btn-secondary {
  background: white;
  color: #333;
  border: 1px solid #d9d9d9;
}

.btn-secondary:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.btn-outline {
  background: white;
  color: #666;
  border: 1px solid #d9d9d9;
}

.btn-outline:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.btn-sm {
  padding: 4px 12px;
  font-size: 12px;
}

/* 项目概览卡片 */
.overview-cards {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.overview-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-icon svg {
  width: 24px;
  height: 24px;
}

.total-icon {
  background: #e6f7ff;
  color: #1890ff;
}

.progress-icon {
  background: #fff7e6;
  color: #fa8c16;
}

.completed-icon {
  background: #f6ffed;
  color: #52c41a;
}

.budget-icon {
  background: #f9f0ff;
  color: #722ed1;
}

.cost-icon {
  background: #fff1f0;
  color: #f5222d;
}

.rate-icon {
  background: #e6f7ff;
  color: #13c2c2;
}

.card-content {
  flex: 1;
}

.card-label {
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
}

.card-value {
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

/* 所有项目/我的项目：分段控件（选中：白底蓝框蓝字） */
.scope-segment {
  display: inline-flex;
  border: 1px solid #d0d3d6;
  border-radius: 6px;
  overflow: hidden;
  background: #f2f3f5;
  flex-shrink: 0;
}

.scope-btn {
  padding: 6px 14px;
  font-size: 13px;
  color: #646a73;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
}

.scope-btn + .scope-btn {
  border-left: 1px solid #d0d3d6;
}

.scope-btn:hover {
  color: #1f2329;
}

.scope-btn.active {
  background: #fff;
  color: #0052d9;
  box-shadow: inset 0 0 0 1px #0052d9;
  position: relative;
  z-index: 1;
}

.scope-btn.active + .scope-btn {
  border-left-color: transparent;
}


.load-error {
  padding: 16px;
  color: #f5222d;
  background: #fff1f0;
  border-radius: 8px;
  margin-bottom: 16px;
}

.phase-deliveries {
  margin-top: 8px;
}

.phase-delivery-list {
  margin: 4px 0 0 0;
  padding-left: 18px;
  font-size: 13px;
  color: #666;
}

.phase-delivery-list li {
  margin-bottom: 2px;
}

.phase-date-item {
  display: block;
  font-size: 11px;
  color: #999;
  margin-top: 4px;
}

.list-header {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.list-header--row1 {
  display: flex;
  align-items: center;
  gap: 16px;
}

.list-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex-wrap: wrap;
}

.list-header--row1 .view-toggle {
  flex-shrink: 0;
}

.project-search-input {
  width: 280px;
  max-width: 100%;
}

.project-search-input :deep(.el-input__wrapper) {
  padding-left: 8px;
  box-shadow: 0 0 0 1px #d0d3d6 inset;
  border-radius: 6px;
}

.project-search-prefix-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: #646a73;
  cursor: pointer;
  line-height: 1;
}

.project-search-prefix-btn:hover {
  color: #1f2329;
}

.list-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin-left: auto;
}

.lifecycle-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.status-tabs-spacer {
  flex: 1;
  min-width: 0;
}
.status-tabs {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.status-tab {
  border: 1px solid #e5e6eb;
  background: #fff;
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 13px;
  color: #4e5969;
  cursor: pointer;
}
.status-tab.active {
  border-color: #3370ff;
  color: #3370ff;
  background: #edf2ff;
}
.status-tab-count {
  margin-left: 4px;
  color: #8f959e;
}
.project-card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  min-height: 22px;
}

.project-card-more {
  position: relative;
  top: auto;
  right: auto;
  z-index: 2;
  flex-shrink: 0;
  margin: -2px -4px 0 0;
}
.project-card-more-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  color: #8f959e;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
}
.project-card-more-btn:hover {
  background: #f2f3f5;
  color: #1f2329;
}
.lifecycle-btn {
  border: 1px solid #d0d3d6;
  background: #fff;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
}
.lifecycle-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.lifecycle-btn--danger {
  color: #c63637;
  border-color: #f0c2c2;
}
.project-card-check {
  display: none;
}
.tag-status.status-delayed {
  background: #fff7e8;
  color: #d46b08;
}
.tag-status.status-void {
  background: #f2f3f5;
  color: #86909c;
}

.overview-entry-btn {
  flex-shrink: 0;
  padding: 6px 14px;
  font-size: 13px;
  font-family: inherit;
  color: #646a73;
  background: #fff;
  border: 1px solid #d0d3d6;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
  line-height: 1.4;
}

.overview-entry-btn:hover {
  background: #f5f5f5;
  color: #1f2329;
  border-color: #c0c4cc;
}

.overview-entry-btn--primary {
  color: #fff;
  background: #0052d9;
  border-color: #0052d9;
}

.overview-entry-btn--primary:hover {
  color: #fff;
  background: #0041b3;
  border-color: #0041b3;
}

.list-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2329;
  margin: 0;
}

.list-count {
  font-size: 13px;
  color: #8f959e;
}

/* 项目卡片网格：自适应列宽，不写死固定 px */
.project-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 260px), 1fr));
  gap: 16px;
  align-items: start;
}

.project-grid > .project-card {
  min-width: 0;
  max-width: 100%;
}

.project-list-pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding-bottom: 4px;
}

.project-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: transform 0.3s, box-shadow 0.3s;
  overflow: hidden;
}

.project-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* 新版项目卡片：底边进度条；仅标题深色，字段偏灰 */
.project-card-redesign {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  padding: 14px 14px 18px;
  border: 1px solid #e8ecf1;
  border-bottom: none;
  box-shadow: none;
  font-size: 13px;
  overflow: hidden;
  border-radius: 8px;
}

.project-card-redesign::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 4px;
  border-radius: 0 0 7px 7px;
  pointer-events: none;
  background: linear-gradient(
    to right,
    #0052d9 0%,
    #0052d9 var(--card-progress, 0%),
    #e5e8eb var(--card-progress, 0%),
    #e5e8eb 100%
  );
}

.project-card-name {
  font-size: 14px;
  font-weight: 600;
  color: #1f2329;
  margin: 0;
  line-height: 1.35;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-card-redesign .project-card-label,
.project-card-redesign .project-card-value {
  color: #8f959e;
}

.project-card-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 13px;
  min-width: 0;
  line-height: 1.4;
}

.project-card-row--date-full {
  flex-wrap: nowrap;
  min-width: 0;
}

.project-card-row--date-full .project-card-label {
  flex-shrink: 0;
}

.project-card-row--date-full .project-card-value--date {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-card-value--date {
  font-size: 12px;
  color: #1f2329;
  white-space: nowrap;
  letter-spacing: 0.01em;
}

.project-card-label {
  color: #8f959e;
  flex-shrink: 0;
  font-size: 13px;
  white-space: nowrap;
  width: 4.5em;
}

.project-card-value {
  color: #8f959e;
  min-width: 0;
  font-size: 13px;
}

.project-card-row--ellipsis-value {
  min-width: 0;
  overflow: hidden;
}

.project-card-value--ellipsis {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-card-value.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  min-width: 0;
  flex: 1;
}

.project-card-tag {
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.project-card-tag.tag-status.status-progress {
  background: #e8f8f0;
  color: #00a870;
}

.project-card-tag.tag-status.status-completed {
  background: #e8f8f0;
  color: #00a870;
}

.project-card-tag.tag-status.status-paused {
  background: #fff7e8;
  color: #d46b08;
}

.project-card-tag.tag-status.status-delayed {
  background: #fff7e8;
  color: #d46b08;
}

.project-card-tag.tag-status.status-void {
  background: #f2f3f5;
  color: #86909c;
}

.project-card-tag.tag-risk.risk-high {
  background: #ffece8;
  color: #e34d59;
}

.project-card-tag.tag-risk.risk-medium {
  background: #fff3c9;
  color: #c9a227;
}

.project-card-tag.tag-risk.risk-low {
  background: #f5f5f5;
  color: #8f959e;
}

.project-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.project-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  flex: 1;
  margin-right: 12px;
}

.status-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.status-progress {
  background: #e6f7ff;
  color: #1890ff;
}

.status-completed {
  background: #f6ffed;
  color: #52c41a;
}

.status-paused {
  background: #fff7e6;
  color: #fa8c16;
}

.project-info {
  margin-bottom: 16px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  margin-bottom: 8px;
}

.info-label {
  color: #999;
}

.info-value {
  color: #333;
}

.project-progress {
  margin-bottom: 16px;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
}

.progress-percent {
  color: #1890ff;
  font-weight: 500;
}

.progress-bar {
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #1890ff, #40a9ff);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.project-budget {
  display: flex;
  justify-content: space-between;
  padding: 12px;
  background: #fafafa;
  border-radius: 6px;
  margin-bottom: 16px;
}

.budget-item {
  text-align: center;
}

.budget-label {
  display: block;
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
}

.budget-value {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.project-actions {
  display: flex;
  gap: 8px;
}

/* AI 面板相关样式已抽离为全局组件，此处保留业务主体样式 */

.panel-header {
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.ai-icon {
  width: 20px;
  height: 20px;
  color: #1890ff;
}

.ai-messages {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  max-height: 500px;
}

.message {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.message-avatar svg {
  width: 16px;
  height: 16px;
}

.message.ai .message-avatar {
  background: #e6f7ff;
  color: #1890ff;
}

.message.user .message-avatar {
  background: #f5f5f5;
  color: #666;
}

.message-content {
  flex: 1;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
}

.message.ai .message-content {
  background: #f7f7f7;
}

.message.user .message-content {
  background: #e6f7ff;
}

.ai-input {
  padding: 16px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  gap: 10px;
}

.input-field {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid #d9d9d9;
  border-radius: 20px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.3s;
}

.input-field:focus {
  border-color: #1890ff;
}

.send-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #1890ff;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s;
}

.send-btn:hover {
  background: #40a9ff;
}

.send-btn svg {
  width: 16px;
  height: 16px;
}

/* 响应式调整 */
@media (max-width: 1400px) {
  .overview-cards {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 1200px) {
  .stage-view {
    flex-direction: column;
  }
  
  .stage-column {
    width: 100%;
  }
}

/* 视图切换按钮 */
.view-toggle {
  display: inline-flex;
  background: #f5f5f5;
  border-radius: 4px;
  padding: 2px;
  gap: 2px;
}

.toggle-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  border: none;
  background: transparent;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.toggle-btn svg {
  width: 16px;
  height: 16px;
  color: #666;
  transition: color 0.2s ease;
}

.toggle-btn:hover svg {
  color: #1890ff;
}

.toggle-btn.active {
  background: white;
}

.toggle-btn.active svg {
  color: #1890ff;
}

/* 阶段视图 */
.stage-view {
  display: flex;
  gap: 8px;
  min-height: 500px;
  align-items: stretch;
}

.stage-column {
  flex: 1;
  min-width: 0;
  background: #fafafa;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.stage-header {
  padding: 12px 12px 0;
  background: white;
  display: flex;
  flex-direction: column;
  min-height: 56px;
}

.stage-header-main {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-height: 40px;
}

.stage-title {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.stage-title-text {
  font-size: 12px;
  font-weight: 600;
  color: #333;
  line-height: 1.35;
  word-break: break-word;
}

.stage-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 4px;
}

.stage-count {
  font-size: 11px;
  color: #999;
  flex-shrink: 0;
  line-height: 1.35;
  padding-top: 2px;
  white-space: nowrap;
}

.stage-header-bar {
  height: 3px;
  width: 100%;
  margin-top: 10px;
  border-radius: 2px 2px 0 0;
  flex-shrink: 0;
}

.stage-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 8px;
}

.stage-cards {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.stage-card {
  background: white;
  border-radius: 6px;
  padding: 10px 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
  overflow: hidden;
}

.stage-card--progress {
  position: relative;
  border: 1px solid #e8ecf1;
  border-bottom: none;
  padding-bottom: 20px;
  box-shadow: none;
}

.stage-card--progress::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 4px;
  border-radius: 0 0 5px 5px;
  pointer-events: none;
  background: linear-gradient(
    to right,
    #0052d9 0%,
    #0052d9 var(--card-progress, 0%),
    #e5e8eb var(--card-progress, 0%),
    #e5e8eb 100%
  );
}

.stage-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stage-card--progress:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.stage-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  min-width: 0;
}

.stage-card-title {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  flex: 1;
  min-width: 0;
  margin-right: 0;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

.stage-card-status {
  flex-shrink: 0;
  font-size: 11px;
  padding: 2px 7px;
}

.stage-card-info {
  margin-bottom: 8px;
  min-width: 0;
  font-size: 12px;
}

.stage-card-info .stage-card-info-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  min-width: 0;
  font-size: 12px;
  margin-bottom: 3px;
}

.stage-card-info .info-label {
  flex-shrink: 0;
  color: #999;
  font-size: 11px;
}

.stage-card-info .info-value--ellipsis {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #333;
}

.stage-card-info .info-value--fixed {
  flex: 0 1 auto;
  min-width: 0;
  color: #333;
}

.stage-card-progress {
  margin-bottom: 10px;
}

.stage-card-progress .progress-header {
  font-size: 12px;
  margin-bottom: 6px;
}

.stage-card-budget {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  font-size: 11px;
  color: #999;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  min-width: 0;
}

.stage-card-budget .phase-date-item {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stage-empty {
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 13px;
}

.stage-column-pagination {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  padding: 8px 0 4px;
  margin-top: 4px;
  border-top: 1px solid #f0f0f0;
}
</style>